<template>
  <router-view />
  <FooterComponent />
</template>

<script setup>
import FooterComponent from "@/components/FooterComponent.vue";
import { useHead } from "@vueuse/head";
import { useRoute } from "vue-router";
import { computed } from "vue";

const route = useRoute();

useHead({
  title: computed(() => route.meta.title || "Merheje Argentina"),
  meta: [
    {
      name: "description",
      content: computed(
        () => route.meta.description || "Distribuidor oficial Merheje Argentina"
      ),
    },
    {
      property: "og:title",
      content: computed(() => route.meta.title || "Merheje Argentina"),
    },
    {
      property: "og:description",
      content: computed(
        () => route.meta.description || "Distribuidor oficial Merheje Argentina"
      ),
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:locale",
      content: "es_AR",
    },
    {
      property: "og:image",
      content: computed(() => {
        // Default to merheje-basic image for home as requested
        if (route.name === "home") {
          return "https://merheje.com.ar/img/og/merheje-basic.png";
        }
        // Use route name for other images
        return `https://merheje.com.ar/img/og/${route.name}.png`;
      }),
    },
  ],
});
</script>
<style>
/* Omnes Font Faces */
@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Regular.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Italic.ttf") format("truetype");
  font-weight: 400;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Hairline.ttf") format("truetype");
  font-weight: 100;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Hairline Italic.ttf") format("truetype");
  font-weight: 100;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Thin.ttf") format("truetype");
  font-weight: 200;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Thin Italic.ttf") format("truetype");
  font-weight: 200;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes ExtraLight.ttf") format("truetype");
  font-weight: 200;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes ExtraLight Italic.ttf") format("truetype");
  font-weight: 200;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Light.ttf") format("truetype");
  font-weight: 300;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Light Italic.ttf") format("truetype");
  font-weight: 300;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Medium.ttf") format("truetype");
  font-weight: 500;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Medium Italic.ttf") format("truetype");
  font-weight: 500;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes SemiBold.ttf") format("truetype");
  font-weight: 600;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes SemiBold Italic.ttf") format("truetype");
  font-weight: 600;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Bold.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Bold Italic.ttf") format("truetype");
  font-weight: 700;
  font-style: italic;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Black.ttf") format("truetype");
  font-weight: 900;
  font-style: normal;
}

@font-face {
  font-family: "Omnes";
  src: url("@/assets/fonts/Omnes Black Italic.ttf") format("truetype");
  font-weight: 900;
  font-style: italic;
}

#app {
  font-family: "Omnes", Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: #8e8ac1;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

body {
  margin: 0;
  overflow-x: hidden;
}

/* Global Responsive Utilities */
* {
  box-sizing: border-box;
}

img {
  max-width: 100%;
  height: auto;
}

/* Prevent horizontal scrolling on small devices */
html {
  overflow-x: hidden;
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Touch-friendly interactions */
@media (hover: none) and (pointer: coarse) {
  .sections-grid div:hover {
    transform: none;
    box-shadow: none;
  }

  .sections-grid div:active {
    transform: scale(0.98);
  }
}

/* Omnes Font Utilities */
* {
  font-family: "Omnes", Arial, sans-serif;
}

.font-hairline {
  font-weight: 100;
}

.font-thin {
  font-weight: 200;
}

.font-extralight {
  font-weight: 200;
}

.font-light {
  font-weight: 300;
}

.font-regular {
  font-weight: 400;
}

.font-medium {
  font-weight: 500;
}

.font-semibold {
  font-weight: 600;
}

.font-bold {
  font-weight: 700;
}

.font-black {
  font-weight: 900;
}

.font-italic {
  font-style: italic;
}
</style>
